import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Upload,
  Trash2,
  FileText,
  Loader2,
  LogOut,
  Download,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, authHeaders, getToken, apiError } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuth";
import { useResourceFiles } from "@/context/ResourceFiles";
import { SLOTS_BY_SERVICE, formatBytes } from "@/content/resourceCatalog";
import { PostsPanel } from "@/components/admin/PostsPanel";
import { UsersPanel } from "@/components/admin/UsersPanel";

const ALL_TABS = [
  { key: "resources", label: "Resource Files", admin: true },
  { key: "blogs", label: "Blogs" },
  { key: "leads", label: "Download Leads", admin: true },
  { key: "consultations", label: "Consultation Requests", admin: true },
  { key: "users", label: "Team Access", admin: true },
];

const fmtDate = (d) =>
  new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const SlotRow = ({ slot, file, onUpload, onDelete, busy }) => {
  const inputRef = React.useRef(null);
  return (
    <div
      className="flex flex-col gap-4 border-t border-white/10 py-5 lg:flex-row lg:items-center"
      data-testid={`admin-slot-${slot.slotKey}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-[#8E9AEA]/35 bg-[#8E9AEA]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8E9AEA]">
            {slot.badge}
          </span>
          {file ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-300">
              <CheckCircle2 className="h-3.5 w-3.5" /> Live on site
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-300">
              <AlertCircle className="h-3.5 w-3.5" /> No file — visitors see "Request a copy"
            </span>
          )}
        </div>
        <p className="mt-2 text-sm font-semibold text-white">{slot.title}</p>
        <p className="mt-1 font-mono text-[11px] text-[#7C86A2]">{slot.slotKey}</p>
        {file && (
          <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#A6B0C9]">
            <span className="inline-flex items-center gap-1.5 text-[#DDE3F2]">
              <FileText className="h-3.5 w-3.5 text-[#8E9AEA]" />
              {file.originalFilename}
            </span>
            <span>{formatBytes(file.size)}</span>
            <span>Uploaded {fmtDate(file.uploadedAt)}</span>
            <span>{file.downloadCount ?? 0} downloads</span>
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Link
          to={slot.servicePath}
          target="_blank"
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-2 text-xs font-semibold text-[#DDE3F2] transition-colors duration-200 hover:border-white/30 hover:text-white"
          data-testid={`admin-view-page-${slot.slotKey}`}
        >
          <ExternalLink className="h-3.5 w-3.5" /> Page
        </Link>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(slot, f);
            e.target.value = "";
          }}
          data-testid={`admin-file-input-${slot.slotKey}`}
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="btn-primary !px-4 !py-2 !text-xs disabled:opacity-60"
          data-testid={`admin-upload-${slot.slotKey}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {file ? "Replace PDF" : "Upload PDF"}
        </button>
        {file && (
          <button
            onClick={() => onDelete(slot)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300 transition-colors duration-200 hover:bg-red-500/20"
            data-testid={`admin-delete-${slot.slotKey}`}
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        )}
      </div>
    </div>
  );
};

const Table = ({ rows, cols, testId, empty }) => (
  <div className="overflow-x-auto rounded-xl border border-white/10" data-testid={testId}>
    {rows.length === 0 ? (
      <p className="px-5 py-10 text-center text-sm text-[#A6B0C9]">{empty}</p>
    ) : (
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.03]">
            {cols.map((c) => (
              <th key={c.key} className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#A6B0C9]">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id} className="border-b border-white/[0.06] last:border-0">
              {cols.map((c) => (
                <td key={c.key} className="px-5 py-4 align-top text-[#DDE3F2]">
                  {c.key === "createdAt" ? fmtDate(r.createdAt) : r[c.key] || "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const PAGE_OPTIONS = [
  { value: "/", label: "Home" },
  { value: "/services", label: "Services (overview)" },
  { value: "/services/vapt", label: "VAPT" },
  { value: "/services/managed-security", label: "Managed Security Services" },
  { value: "/services/solutions", label: "Cybersecurity Solutions (overview)" },
  { value: "/services/enterprise-landing-zone", label: "Enterprise Landing Zone" },
  { value: "/services/enterprise-siem", label: "Enterprise SIEM" },
  { value: "/services/enterprise-xdr", label: "Enterprise XDR" },
  { value: "/services/cloud-security", label: "Cloud Security" },
  { value: "/services/open-source-soc", label: "Open Source SOC Model" },
  { value: "/about", label: "About" },
  { value: "/industries", label: "Industries" },
  { value: "/case-studies", label: "Engagement Scenarios" },
  { value: "/resources", label: "Resources page only" },
];

const BADGE_OPTIONS = [
  "Free Whitepaper",
  "Free Checklist",
  "Datasheet",
  "Architecture Guide",
  "Compliance Guide",
  "Sample Report",
  "Free Resource",
];

const AddResourceForm = ({ onCreated }) => {
  const [form, setForm] = React.useState({
    pagePath: "/services/vapt",
    badge: "Free Whitepaper",
    title: "",
    description: "",
  });
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a PDF first.");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      await api.post("/admin/custom-resources", fd, { headers: authHeaders() });
      toast.success(`"${form.title}" added to ${form.pagePath}`);
      setForm({ ...form, title: "", description: "" });
      setFile(null);
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field =
    "mt-2 w-full rounded-lg border border-white/12 bg-[#0B0F1A] px-4 py-3 text-sm text-white outline-none transition-colors duration-200 placeholder:text-[#7C86A2] focus:border-[#8E9AEA]/60";
  const label = "text-[11px] font-bold uppercase tracking-[0.18em] text-[#A6B0C9]";

  return (
    <form onSubmit={submit} className="rounded-xl border border-[#8E9AEA]/30 bg-[#8E9AEA]/[0.05] p-5 sm:p-7" data-testid="add-resource-form">
      <h2 className="font-display text-lg font-bold text-white">Add a new resource to any page</h2>
      <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">
        Upload a PDF with your own title and description. It appears in the Resources section of the page you choose
        (and on the Resources page), with the same email-capture download flow.
      </p>
      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <label>
          <span className={label}>Page</span>
          <select
            value={form.pagePath}
            onChange={(e) => setForm({ ...form, pagePath: e.target.value })}
            className={field}
            data-testid="add-resource-page"
          >
            {PAGE_OPTIONS.map((p) => (
              <option key={p.value} value={p.value} className="bg-[#0B0F1A]">
                {`${p.label} — ${p.value}`}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={label}>Badge</span>
          <select
            value={form.badge}
            onChange={(e) => setForm({ ...form, badge: e.target.value })}
            className={field}
            data-testid="add-resource-badge"
          >
            {BADGE_OPTIONS.map((b) => (
              <option key={b} value={b} className="bg-[#0B0F1A]">
                {b}
              </option>
            ))}
          </select>
        </label>
        <label className="lg:col-span-2">
          <span className={label}>Title</span>
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Ransomware Readiness Assessment Guide"
            className={field}
            data-testid="add-resource-title"
          />
        </label>
        <label className="lg:col-span-2">
          <span className={label}>Description</span>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="One or two lines describing what the reader gets."
            className={field}
            data-testid="add-resource-description"
          />
        </label>
        <label className="lg:col-span-2">
          <span className={label}>PDF file</span>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className={`${field} file:mr-4 file:rounded-md file:border-0 file:bg-[#4C5A8C] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white`}
            data-testid="add-resource-file"
          />
        </label>
      </div>
      <button type="submit" disabled={busy} className="btn-primary mt-6 disabled:opacity-60" data-testid="add-resource-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        {busy ? "Uploading…" : "Add resource"}
      </button>
    </form>
  );
};

export default function Admin() {
  const { user, logout } = useAdminAuth();
  const { refresh: refreshPublic } = useResourceFiles();
  const [tab, setTab] = React.useState(() =>
    localStorage.getItem("lanvaus_admin_tab") || "resources"
  );

  React.useEffect(() => {
    localStorage.setItem("lanvaus_admin_tab", tab);
  }, [tab]);

  React.useEffect(() => {
    if (user && user.role !== "admin" && tab !== "blogs") setTab("blogs");
  }, [user, tab]);
  const [files, setFiles] = React.useState({});
  const [customFiles, setCustomFiles] = React.useState([]);
  const [leads, setLeads] = React.useState([]);
  const [consultations, setConsultations] = React.useState([]);
  const [busySlot, setBusySlot] = React.useState(null);

  const loadFiles = React.useCallback(async () => {
    const [slots, custom] = await Promise.all([
      api.get("/admin/resources", { headers: authHeaders() }),
      api.get("/custom-resources"),
    ]);
    setFiles(Object.fromEntries(slots.data.map((f) => [f.slotKey, f])));
    setCustomFiles(custom.data);
  }, []);

  React.useEffect(() => {
    if (!user || user.role !== "admin") return;
    loadFiles().catch((e) => toast.error(apiError(e)));
    api.get("/admin/leads", { headers: authHeaders() }).then(({ data }) => setLeads(data)).catch(() => {});
    api
      .get("/admin/consultations", { headers: authHeaders() })
      .then(({ data }) => setConsultations(data))
      .catch(() => {});
  }, [user, loadFiles]);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#8E9AEA]" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const onUpload = async (slot, file) => {
    setBusySlot(slot.slotKey);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("slotKey", slot.slotKey);
    fd.append("servicePath", slot.servicePath);
    fd.append("serviceLabel", slot.serviceLabel);
    fd.append("title", slot.title);
    fd.append("badge", slot.badge);
    try {
      await api.post("/admin/resources", fd, { headers: authHeaders() });
      await loadFiles();
      refreshPublic();
      toast.success(`Uploaded "${file.name}"`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusySlot(null);
    }
  };

  const onDelete = async (slot) => {
    if (!window.confirm(`Delete the current PDF for "${slot.title}"? Visitors will see "Request a copy" instead.`))
      return;
    try {
      await api.delete(`/admin/resources/${slot.slotKey}`, { headers: authHeaders() });
      await loadFiles();
      refreshPublic();
      toast.success("File removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onDeleteCustom = async (item) => {
    if (!window.confirm(`Delete "${item.title}" from ${item.pagePath}?`)) return;
    try {
      await api.delete(`/admin/custom-resources/${item.slotKey}`, { headers: authHeaders() });
      await loadFiles();
      refreshPublic();
      toast.success("Resource removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const totalSlots = SLOTS_BY_SERVICE.reduce((n, g) => n + g.slots.length, 0);
  const liveCount = Object.keys(files).length;
  const isAdmin = user.role === "admin";
  const TABS = ALL_TABS.filter((t) => isAdmin || !t.admin);
  const STATS = isAdmin
    ? [
        ["Resource slots", `${liveCount} / ${totalSlots} live`],
        ["Custom resources", String(customFiles.length)],
        ["Download leads", String(leads.length)],
        ["Consultation requests", String(consultations.length)],
      ]
    : [["Signed in as", user.role]];

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0F1A]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link">
            <img src="/assets/lanvaus-mark.png" alt="Lanvaus logo" width="323" height="260" className="h-7 w-auto" />
            <img src="/assets/lanvaus-wordmark.png" alt="Lanvaus" width="778" height="120" className="h-[18px] w-auto" />
            <span className="hidden text-xs uppercase tracking-[0.2em] text-[#8E9AEA] sm:inline">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-[#A6B0C9] sm:inline" data-testid="admin-user-email">
              {user.email}
            </span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-lg border border-white/12 px-3.5 py-2 text-xs font-semibold text-[#DDE3F2] transition-colors duration-200 hover:border-white/30 hover:text-white"
              data-testid="admin-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map(([label, value]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-5" data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#A6B0C9]">{label}</p>
              <p className="mt-2 font-display text-2xl font-bold text-white">{value}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                tab === t.key ? "border-[#8E9AEA] text-white" : "border-transparent text-[#A6B0C9] hover:text-white"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "resources" && isAdmin && (
          <div className="mt-8" data-testid="admin-resources-panel">
            <AddResourceForm
              onCreated={async () => {
                await loadFiles();
                refreshPublic();
              }}
            />

            {customFiles.length > 0 && (
              <section className="mt-10" data-testid="admin-custom-list">
                <h2 className="font-display text-lg font-bold text-white">Resources you added ({customFiles.length})</h2>
                <div className="mt-2">
                  {customFiles.map((c) => (
                    <div
                      key={c.slotKey}
                      className="flex flex-col gap-4 border-t border-white/10 py-5 lg:flex-row lg:items-center"
                      data-testid={`admin-custom-${c.slotKey}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-[#8E9AEA]/35 bg-[#8E9AEA]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8E9AEA]">
                            {c.badge}
                          </span>
                          <span className="font-mono text-[11px] text-[#7C86A2]">{c.pagePath}</span>
                        </div>
                        <p className="mt-2 text-sm font-semibold text-white">{c.title}</p>
                        {c.description && <p className="mt-1 text-xs leading-relaxed text-[#A6B0C9]">{c.description}</p>}
                        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#A6B0C9]">
                          <span className="inline-flex items-center gap-1.5 text-[#DDE3F2]">
                            <FileText className="h-3.5 w-3.5 text-[#8E9AEA]" />
                            {c.originalFilename}
                          </span>
                          <span>{formatBytes(c.size)}</span>
                          <span>Uploaded {fmtDate(c.uploadedAt)}</span>
                          <span>{c.downloadCount ?? 0} downloads</span>
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Link
                          to={c.pagePath}
                          target="_blank"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-2 text-xs font-semibold text-[#DDE3F2] transition-colors duration-200 hover:border-white/30 hover:text-white"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Page
                        </Link>
                        <button
                          onClick={() => onDeleteCustom(c)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300 transition-colors duration-200 hover:bg-red-500/20"
                          data-testid={`admin-custom-delete-${c.slotKey}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <p className="mt-12 max-w-3xl text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">
              Below are the built-in resource slots that already exist on the website. Upload a PDF to make a slot's
              Download button live; delete it later to take it down instantly. Files are stored securely and only
              served after a visitor submits their details.
            </p>
            {SLOTS_BY_SERVICE.map((group) => (
              <section key={group.path} className="mt-10" data-testid={`admin-group-${group.path.split("/").pop()}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-display text-lg font-bold text-white">{group.label}</h2>
                  <span className="font-mono text-[11px] text-[#7C86A2]">{group.path}</span>
                </div>
                <div className="mt-2">
                  {group.slots.map((slot) => (
                    <SlotRow
                      key={slot.slotKey}
                      slot={slot}
                      file={files[slot.slotKey]}
                      onUpload={onUpload}
                      onDelete={onDelete}
                      busy={busySlot === slot.slotKey}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {tab === "blogs" && <PostsPanel />}
        {tab === "users" && <UsersPanel />}

        {(tab === "leads" || tab === "consultations") && (
          <div className="mt-8">
            <div className="mb-5 flex justify-end">
              <a
                href={`${API}/admin/export.csv?type=${tab}&token=${getToken()}`}
                className="btn-secondary !px-4 !py-2 !text-xs"
                data-testid={`admin-export-${tab}`}
              >
                <Download className="h-3.5 w-3.5" /> Export CSV
              </a>
            </div>
            {tab === "leads" ? (
              <Table
                testId="admin-leads-table"
                rows={leads}
                empty="No resource downloads yet. Leads appear here the moment someone requests a PDF."
                cols={[
                  { key: "createdAt", label: "When" },
                  { key: "name", label: "Name" },
                  { key: "email", label: "Email" },
                  { key: "company", label: "Company" },
                  { key: "phone", label: "Phone" },
                  { key: "resourceTitle", label: "Resource" },
                  { key: "serviceLabel", label: "Service" },
                ]}
              />
            ) : (
              <Table
                testId="admin-consultations-table"
                rows={consultations}
                empty="No consultation requests yet."
                cols={[
                  { key: "createdAt", label: "When" },
                  { key: "name", label: "Name" },
                  { key: "email", label: "Email" },
                  { key: "company", label: "Company" },
                  { key: "phone", label: "Phone" },
                  { key: "service", label: "Service" },
                  { key: "message", label: "Message" },
                ]}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
